import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Edit2, Trash2, MapPin, Zap, Shield, DollarSign, Eye, EyeOff } from 'lucide-react';
import { facilities } from '../../data/facilities';
import { LocationEditModal } from './modals/LocationEditModal';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { removeLocationFromUser, updateFacility } from '../../utils/userService';
import { useAuth } from '../../hooks/useAuth';
import { toast } from 'react-hot-toast';

interface LocationListProps {
  locations: string[];
  onAddLocation: () => void;
}

export function LocationList({ locations, onAddLocation }: LocationListProps) {
  const { user } = useAuth();
  const [editingLocation, setEditingLocation] = useState<any>(null);
  const [deletingLocation, setDeletingLocation] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);

  const userFacilities = facilities.filter(facility => 
    locations.includes(facility.id)
  );

  const handleDeleteLocation = async () => {
    if (!user || !deletingLocation) return;

    try {
      setIsLoading(true);
      await removeLocationFromUser(user.uid, deletingLocation.id);
      toast.success('Location removed successfully');
      setDeletingLocation(null);
    } catch (error) {
      console.error('Error removing location:', error);
      toast.error('Failed to remove location');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePublishToggle = async (facility: any) => {
    try {
      const updatedFacility = {
        ...facility,
        isPublished: !facility.isPublished
      };
      await updateFacility(updatedFacility);
      toast.success(`Location ${updatedFacility.isPublished ? 'published' : 'unpublished'} successfully`);
    } catch (error) {
      console.error('Error updating publish status:', error);
      toast.error('Failed to update publish status');
    }
  };

  return (
    <div className="space-y-4">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
      >
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Facility
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Location
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Capacity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Power Cost
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Security
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Published
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {userFacilities.map((facility) => (
                <motion.tr
                  key={facility.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="hover:bg-gray-50"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <img
                          src={facility.image}
                          alt={facility.name}
                          className="h-10 w-10 rounded-lg object-cover"
                        />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {facility.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          ID: {facility.id}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <MapPin className="w-4 h-4 text-gray-400 mr-2" />
                      <div>
                        <div className="text-sm text-gray-900">{facility.location}</div>
                        <div className="text-sm text-gray-500">{facility.country}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Zap className="w-4 h-4 text-yellow-400 mr-2" />
                      <span className="text-sm text-gray-900">{facility.capacity}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <DollarSign className="w-4 h-4 text-green-400 mr-2" />
                      <span className="text-sm text-gray-900">${facility.powerCost}/kWh</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Shield className="w-4 h-4 text-purple-400 mr-2" />
                      <span className="text-sm text-gray-900 capitalize">
                        {facility.securityLevel}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex justify-center">
                      <button
                        onClick={() => handlePublishToggle(facility)}
                        className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                          facility.isPublished
                            ? 'bg-green-100 text-green-800 hover:bg-green-200'
                            : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                        }`}
                      >
                        {facility.isPublished ? (
                          <>
                            <Eye className="w-4 h-4 mr-1" />
                            Published
                          </>
                        ) : (
                          <>
                            <EyeOff className="w-4 h-4 mr-1" />
                            Draft
                          </>
                        )}
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => setEditingLocation(facility)}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setDeletingLocation(facility)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Edit Modal */}
      {editingLocation && (
        <LocationEditModal
          facility={editingLocation}
          onClose={() => setEditingLocation(null)}
          onSave={async (updatedFacility) => {
            try {
              await updateFacility(updatedFacility);
              toast.success('Location updated successfully');
              setEditingLocation(null);
            } catch (error) {
              console.error('Error updating location:', error);
              toast.error('Failed to update location');
            }
          }}
        />
      )}

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={!!deletingLocation}
        onClose={() => setDeletingLocation(null)}
        onConfirm={handleDeleteLocation}
        title="Remove Location"
        message={`Are you sure you want to remove ${deletingLocation?.name}? This action cannot be undone.`}
        confirmLabel={isLoading ? 'Removing...' : 'Remove'}
        confirmVariant="danger"
      />
    </div>
  );
}